<template>
  <section class="gt-page redemptions-page">
    <div class="gt-page-inner wide">
      <div class="hero gt-card">
        <div>
          <span class="gt-pill">Reportes de lealtad</span>
          <h1>Canjes de puntos</h1>
          <p class="gt-muted">Consulta cupones emitidos, canjes consumidos en PV, productos entregados y descuentos aplicados al 100%.</p>
        </div>
        <div class="hero-actions">
          <button class="gt-button soft" @click="$router.push('/admin/lealtad')">Configuracion</button>
          <button class="gt-button primary" :disabled="loading" @click="loadReport">Actualizar</button>
        </div>
      </div>

      <div v-if="message" class="alert" :class="messageType">{{ message }}</div>

      <section class="gt-card filters-card">
        <div class="filters-grid">
          <label>
            <span>Desde</span>
            <input v-model="filters.from" type="date" />
          </label>
          <label>
            <span>Hasta</span>
            <input v-model="filters.to" type="date" />
          </label>
          <label>
            <span>Estatus</span>
            <select v-model="filters.status">
              <option value="redeemed">Canjeados en PV</option>
              <option value="issued">Emitidos</option>
              <option value="locked">Abiertos en PV</option>
              <option value="expired">Expirados</option>
              <option value="cancelled">Cancelados</option>
              <option value="all">Todos</option>
            </select>
          </label>
          <label>
            <span>Sitio externo</span>
            <select v-model="filters.site_id">
              <option value="">Todos</option>
              <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
            </select>
          </label>
          <label class="search-field">
            <span>Buscar</span>
            <input v-model="filters.q" placeholder="Cliente, ticket o UUID" @keyup.enter="loadReport" />
          </label>
          <button class="gt-button primary filter-button" :disabled="loading" @click="loadReport">Aplicar filtros</button>
        </div>
      </section>

      <section class="gt-grid cols-4 stats-grid">
        <article class="gt-card stat-card main">
          <span>Canjes</span>
          <strong>{{ summary.redemptions || 0 }}</strong>
          <small>{{ filters.status === 'redeemed' ? 'Consumidos en PV' : 'Resultados del filtro' }}</small>
        </article>
        <article class="gt-card stat-card">
          <span>Puntos usados</span>
          <strong>{{ formatNumber(summary.points_spent || 0) }}</strong>
          <small>Equivalente a dinero aplicado</small>
        </article>
        <article class="gt-card stat-card">
          <span>Descuento total</span>
          <strong>{{ money(summary.discount_amount || 0) }}</strong>
          <small>Ventas con 100% de descuento</small>
        </article>
        <article class="gt-card stat-card">
          <span>Productos entregados</span>
          <strong>{{ formatNumber(summary.items_quantity || 0) }}</strong>
          <small>Unidades dentro de canjes</small>
        </article>
      </section>

      <section class="status-strip gt-card">
        <div v-for="item in statusCards" :key="item.key" class="status-chip" :class="item.key">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </section>

      <div class="report-grid">
        <section class="gt-card table-card">
          <div class="section-title-row">
            <div>
              <h2>Detalle de canjes</h2>
              <p class="gt-muted">Cada fila representa un QR generado desde Afrogato y su estado contra Gestion Total.</p>
            </div>
            <button class="gt-button soft" :disabled="!redemptions.length" @click="copyCsv">Copiar CSV</button>
          </div>

          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Sitio</th>
                  <th>Productos</th>
                  <th>Puntos</th>
                  <th>Descuento</th>
                  <th>Estatus</th>
                  <th>Ticket</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in redemptions" :key="row.id">
                  <td>
                    <strong>{{ shortDate(row.redeemed_at || row.created_at) }}</strong>
                    <small>{{ row.redeemed_at ? 'redimido' : 'emitido' }}</small>
                  </td>
                  <td>
                    <strong>{{ row.customer_name || 'Cliente wallet' }}</strong>
                    <small>{{ row.customer_phone || row.customer_email || row.redemption_uuid }}</small>
                  </td>
                  <td>{{ row.external_site_name || 'Sitio externo' }}</td>
                  <td>
                    <div class="items-preview">
                      <span v-for="(item, idx) in normalizeItems(row.items).slice(0, 2)" :key="idx">{{ item.quantity || 1 }}x {{ item.product_name || item.name }}</span>
                      <small v-if="normalizeItems(row.items).length > 2">+{{ normalizeItems(row.items).length - 2 }} mas</small>
                    </div>
                  </td>
                  <td><strong>{{ row.points_spent }}</strong></td>
                  <td><strong>{{ money(row.discount_amount) }}</strong></td>
                  <td><span class="badge" :class="row.status">{{ statusLabel(row.status) }}</span></td>
                  <td>
                    <strong>{{ row.ticket_number || 'Pendiente' }}</strong>
                    <small>{{ row.ticket_uuid || row.redemption_uuid }}</small>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!redemptions.length" class="empty-box">No hay canjes con estos filtros.</div>
          </div>
        </section>

        <aside class="gt-card products-card">
          <div class="section-title-row compact-title">
            <div>
              <h2>Top productos</h2>
              <p class="gt-muted">Lo que mas se esta canjeando.</p>
            </div>
          </div>
          <div class="product-list">
            <div v-for="item in itemSummary" :key="item.name" class="product-row">
              <div>
                <strong>{{ item.name }}</strong>
                <small>{{ formatNumber(item.points_spent || 0) }} puntos</small>
              </div>
              <span>{{ formatNumber(item.quantity || 0) }}</span>
            </div>
            <div v-if="!itemSummary.length" class="empty-mini">Sin productos para resumir.</div>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';

function todayKey() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
}

export default {
  name: 'LoyaltyRedemptionsReportView',
  data() {
    const today = todayKey();
    return {
      loading: false,
      message: '',
      messageType: 'success',
      filters: {
        from: today,
        to: today,
        status: 'redeemed',
        site_id: '',
        q: '',
      },
      summary: {},
      itemSummary: [],
      sites: [],
      redemptions: [],
    };
  },
  computed: {
    statusCards() {
      return [
        { key: 'issued', label: 'Emitidos', value: this.summary.issued || 0 },
        { key: 'locked', label: 'Abiertos', value: this.summary.locked || 0 },
        { key: 'redeemed', label: 'Canjeados', value: this.summary.redeemed || 0 },
        { key: 'expired', label: 'Expirados', value: this.summary.expired || 0 },
        { key: 'cancelled', label: 'Cancelados', value: this.summary.cancelled || 0 },
      ];
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    async loadReport() {
      this.loading = true;
      try {
        const params = { ...this.filters };
        if (!params.site_id) delete params.site_id;
        const { data } = await api.get('/api/pos/loyalty/reports/redemptions', { params });
        this.summary = data.summary || {};
        this.itemSummary = data.item_summary || [];
        this.sites = data.sites || [];
        this.redemptions = data.redemptions || [];
      } catch (error) {
        this.show(error.response?.data?.error || 'No se pudo cargar el reporte de canjes.', 'error');
      } finally {
        this.loading = false;
      }
    },
    normalizeItems(items) {
      return Array.isArray(items) ? items : [];
    },
    statusLabel(status) {
      const map = { issued: 'Emitido', locked: 'Abierto', redeemed: 'Canjeado', cancelled: 'Cancelado', expired: 'Expirado' };
      return map[status] || status;
    },
    shortDate(value) {
      if (!value) return 'Sin fecha';
      return String(value).replace('T', ' ').slice(0, 16);
    },
    money(value) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value || 0));
    },
    formatNumber(value) {
      return new Intl.NumberFormat('es-MX').format(Number(value || 0));
    },
    async copyCsv() {
      const header = ['fecha', 'cliente', 'sitio', 'productos', 'puntos', 'descuento', 'estatus', 'ticket'];
      const lines = this.redemptions.map((row) => {
        const products = this.normalizeItems(row.items).map((item) => `${item.quantity || 1}x ${item.product_name || item.name}`).join(' | ');
        return [
          row.redeemed_at || row.created_at || '',
          row.customer_name || '',
          row.external_site_name || '',
          products,
          row.points_spent || 0,
          row.discount_amount || 0,
          row.status || '',
          row.ticket_number || row.ticket_uuid || '',
        ].map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',');
      });
      const csv = [header.join(','), ...lines].join('\n');
      try {
        await navigator.clipboard.writeText(csv);
        this.show('CSV copiado al portapapeles.', 'success');
      } catch (error) {
        this.show('No se pudo copiar el CSV en este navegador.', 'error');
      }
    },
    show(text, type = 'success') {
      this.message = text;
      this.messageType = type;
      window.clearTimeout(this._msgTimer);
      this._msgTimer = window.setTimeout(() => { this.message = ''; }, 5000);
    },
  },
};
</script>

<style lang="scss" scoped>
.wide { max-width: 1380px; }
.hero { padding: 2rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.hero-actions { display: flex; gap: .75rem; flex-wrap: wrap; }
h1 { margin: .75rem 0 .35rem; font-size: clamp(2rem, 4vw, 3.4rem); }
.alert { margin-top: 1rem; border-radius: 18px; padding: 1rem; font-weight: 900; }
.alert.success { background: #dcfce7; color: #166534; }
.alert.error { background: #fee2e2; color: #991b1b; }
.filters-card { margin-top: 1rem; padding: 1.2rem; }
.filters-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: .8rem; align-items: end; }
.search-field { grid-column: span 2; }
label span { display: block; color: var(--gt-muted); font-size: .78rem; font-weight: 900; margin-bottom: .35rem; }
input, select { width: 100%; border: 1px solid var(--gt-border); border-radius: 16px; padding: .85rem 1rem; background: rgba(255,255,255,.82); outline: none; font-weight: 800; }
.filter-button { height: 3.05rem; }
.stats-grid { margin-top: 1rem; }
.gt-grid.cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.stat-card { padding: 1.25rem; position: relative; overflow: hidden; }
.stat-card.main { background: linear-gradient(135deg, rgba(29,53,87,.96), rgba(69,123,157,.9)); color: #fff; }
.stat-card span { color: var(--gt-muted); font-weight: 900; }
.stat-card.main span, .stat-card.main small { color: rgba(255,255,255,.74); }
.stat-card strong { display: block; margin-top: .55rem; font-size: clamp(1.6rem, 3.5vw, 2.6rem); }
.stat-card small { color: var(--gt-muted); font-weight: 800; }
.status-strip { margin-top: 1rem; padding: .8rem; display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: .65rem; }
.status-chip { border-radius: 18px; background: rgba(255,255,255,.7); border: 1px solid var(--gt-border); padding: .8rem; display: flex; justify-content: space-between; align-items: center; gap: .7rem; }
.status-chip span { font-weight: 900; color: var(--gt-muted); }
.status-chip strong { font-size: 1.25rem; }
.report-grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 1rem; margin-top: 1rem; align-items: start; }
.table-card, .products-card { padding: 1.25rem; }
.section-title-row { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; margin-bottom: 1rem; }
.compact-title { margin-bottom: .7rem; }
h2 { margin: 0 0 .25rem; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: separate; border-spacing: 0 .55rem; min-width: 980px; }
th { text-align: left; color: var(--gt-muted); font-size: .76rem; text-transform: uppercase; letter-spacing: .05em; padding: 0 .75rem; }
td { background: rgba(255,255,255,.72); border-top: 1px solid var(--gt-border); border-bottom: 1px solid var(--gt-border); padding: .85rem .75rem; vertical-align: top; font-weight: 800; }
td:first-child { border-left: 1px solid var(--gt-border); border-radius: 16px 0 0 16px; }
td:last-child { border-right: 1px solid var(--gt-border); border-radius: 0 16px 16px 0; }
td small, .items-preview small { display: block; color: var(--gt-muted); font-weight: 800; margin-top: .2rem; }
.items-preview { display: grid; gap: .25rem; }
.badge { display: inline-flex; border-radius: 999px; padding: .35rem .7rem; font-size: .78rem; font-weight: 1000; }
.badge.redeemed { background: #dcfce7; color: #166534; }
.badge.issued { background: #dbeafe; color: #1d4ed8; }
.badge.locked { background: #fef3c7; color: #92400e; }
.badge.expired, .badge.cancelled { background: #fee2e2; color: #991b1b; }
.product-list { display: grid; gap: .65rem; }
.product-row { display: flex; justify-content: space-between; gap: 1rem; align-items: center; border: 1px solid var(--gt-border); border-radius: 18px; padding: .9rem; background: rgba(255,255,255,.72); }
.product-row small { color: var(--gt-muted); display: block; margin-top: .2rem; }
.product-row span { min-width: 3rem; height: 3rem; border-radius: 16px; background: var(--gt-primary-soft); color: var(--gt-primary); display: grid; place-items: center; font-weight: 1000; }
.empty-box, .empty-mini { border: 1px dashed var(--gt-border); border-radius: 18px; padding: 1.2rem; color: var(--gt-muted); font-weight: 900; text-align: center; }
@media (max-width: 1100px) {
  .filters-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gt-grid.cols-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .search-field { grid-column: span 2; }
  .status-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .report-grid { grid-template-columns: 1fr; }
}
@media (max-width: 760px) {
  .hero { flex-direction: column; align-items: flex-start; }
  .gt-grid.cols-4 { grid-template-columns: 1fr; }
  .filters-grid, .search-field, .status-strip { grid-template-columns: 1fr; grid-column: span 1; }
}
</style>
